import React from 'react';
import { CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { DeploymentStage as StageType } from '../../../types/wordpress.type';

interface DeploymentStageProps {
  stage: StageType;
  onToggleExpand: () => void;
}

const DeploymentStage: React.FC<DeploymentStageProps> = ({ stage, onToggleExpand }) => {
  const getStatusIcon = () => {
    switch (stage.status) {
      case 'completed':
        return <CheckCircleOutlined className="text-green-600 text-lg" />;
      case 'in-progress':
        return <SyncOutlined spin className="text-blue-600 text-lg" />;
      case 'failed':
        return <CloseCircleOutlined className="text-red-600 text-lg" />;
      default:
        return <ClockCircleOutlined className="text-gray-400 text-lg" />;
    }
  };

  const getStatusColor = () => {
    switch (stage.status) {
      case 'completed':
        return 'border-l-green-500 bg-green-50/30';
      case 'in-progress':
        return 'border-l-blue-500 bg-blue-50/30';
      case 'failed':
        return 'border-l-red-500 bg-red-50/30';
      default:
        return 'border-l-gray-300 bg-white';
    }
  };

  const getSubstepIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined className="text-green-600 text-sm" />;
      case 'in-progress':
        return <SyncOutlined spin className="text-blue-600 text-sm" />;
      case 'failed':
        return <CloseCircleOutlined className="text-red-600 text-sm" />;
      default:
        return <div className="w-2 h-2 bg-gray-300 rounded-full" />;
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div
      className={`border-l-4 ${getStatusColor()} rounded-lg transition-all duration-200 mb-3`}
    >
      <button
        onClick={onToggleExpand}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1">
          {getStatusIcon()}
          <div className="text-left">
            <p className="font-medium text-gray-900">{stage.name}</p>
            {stage.status === 'in-progress' && stage.substeps.length > 0 && (
              <div className="mt-1">
                <div className="w-48 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{
                      width: `${
                        (stage.substeps.filter((s) => s.status === 'completed').length /
                          stage.substeps.length) *
                        100
                      }%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {stage.duration && stage.status === 'completed' && (
            <span className="text-xs text-gray-500">{formatDuration(stage.duration)}</span>
          )}
          {stage.expandable && (
            <span className="text-gray-400">
              {stage.expanded ? '▼' : '▶'}
            </span>
          )}
        </div>
      </button>

      {/* Expanded Substeps */}
      {stage.expanded && stage.substeps.length > 0 && (
        <div className="px-4 pb-3 pt-1 space-y-2 border-t border-gray-100 bg-gray-50/50">
          {stage.substeps.map((substep) => (
            <div key={substep.id} className="flex items-center gap-3 pl-8 py-1.5">
              {getSubstepIcon(substep.status)}
              <span
                className={`text-sm ${
                  substep.status === 'completed'
                    ? 'text-gray-700'
                    : substep.status === 'in-progress'
                    ? 'text-blue-700 font-medium'
                    : 'text-gray-500'
                }`}
              >
                {substep.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeploymentStage;